<script>
import {computed, ref, watch} from "vue";
import useTools from "@/use/Tools";
import useSharedData from "@/use/SharedData";

export default {
    props: {
        value: [String, Array],      // String in single mode, Array in multiple mode
        label: String,
        color: String,
        min: String,
        readonly: Boolean,
        disabled: Boolean,
        birthDayPicker: Boolean,
        rules: Array,
        hint: String,
        persistentHint: Boolean,
        multiple: {                  // ← new prop
            type: Boolean,
            default: false,
        },
    },

    emits: ["input", "change"],

    setup(props, {emit}) {
        const {$moment} = useTools();
        const {inputColor, backgroundColor} = useSharedData();

        const modal = ref(false);
        const activePicker = ref(null);

        // date is a String in single mode, Array<String> in multiple mode
        const date = ref(props.multiple ? [] : null);

        // ── Helpers ──────────────────────────────────────────────

        function formatSingle(val) {
            return val ? $moment(val).format("DD-MM-YYYY") : null;
        }

        // Single-mode computed label shown in the text-field
        const computedDateFormattedMoment = computed(() =>
            !props.multiple && date.value ? formatSingle(date.value) : null
        );

        // ── Actions ──────────────────────────────────────────────

        function save() {
            if (!props.multiple)
                modal.value = false;
            emit("input", date.value);
        }

        function clear() {
            date.value = props.multiple ? [] : null;
            emit("input", date.value);
        }

        function change() {
            emit("change");
        }

        // ── Watchers ─────────────────────────────────────────────

        watch(
            () => modal.value,
            () => {
                if (props.birthDayPicker)
                    modal.value && setTimeout(() => (activePicker.value = "YEAR"));
            }
        );

        // Sync external v-model → internal date ref
        watch(
            () => props.value,
            (val) => {
                if (props.multiple) {
                    // Expect an array of ISO strings from the parent
                    date.value = Array.isArray(val)
                        ? val.map((d) => $moment(d).format("YYYY-MM-DD"))
                        : [];
                } else {
                    date.value = val ? $moment(val).format("YYYY-MM-DD") : null;
                }
            },
            {immediate: true}
        );

        return {
            date,
            activePicker,
            modal,
            inputColor,
            backgroundColor,
            computedDateFormattedMoment,
            formatSingle,
            save,
            clear,
            change,
        };
    },
};
</script>

<template>
    <div>
        <v-menu
            v-model="modal"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="290px"
        >
            <template v-slot:activator="{ on, attrs }">
                <!-- ── MULTIPLE MODE: combobox with chips ── -->
                <v-combobox
                    v-if="multiple"
                    :value="date"
                    :label="label"
                    prepend-inner-icon="mdi-calendar"
                    multiple
                    chips
                    small-chips
                    readonly
                    :disabled="disabled"
                    v-bind="attrs"
                    v-on="on"
                    :color="color ? color : inputColor"
                    outlined
                    :background-color="color ? `${color} lighten-5` : backgroundColor"
                    :clearable="!readonly"
                    @click:clear="clear"
                    dense
                    :hint="hint"
                    :persistent-hint="persistentHint"
                    hide-details="auto"
                    :rules="rules"
                >
                    <!-- Format each chip label via moment -->
                    <template v-slot:selection="{ item }">
                        <v-chip small>{{ formatSingle(item) }}</v-chip>
                    </template>
                </v-combobox>

                <!-- ── SINGLE MODE: original text-field ── -->
                <v-text-field
                    v-else
                    :value="computedDateFormattedMoment"
                    :label="label"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    placeholder="e.g 06-07-2022"
                    :disabled="disabled"
                    v-bind="attrs"
                    v-on="on"
                    :color="color ? color : inputColor"
                    outlined
                    :background-color="color ? `${color} lighten-5` : backgroundColor"
                    :clearable="!readonly"
                    @click:clear="clear"
                    dense
                    :hint="hint"
                    :persistent-hint="persistentHint"
                    hide-details="auto"
                    :rules="rules"
                />
            </template>

            <v-date-picker
                v-model="date"
                :multiple="multiple"
                :max="birthDayPicker ? (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10) : null"
                :active-picker.sync="activePicker"
                :readonly="readonly"
                :disabled="disabled"
                :color="color ? color : inputColor"
                locale="fr-fr"
                @input="save"
                @change="change"
            />
        </v-menu>
        <input type="hidden"/>
    </div>
</template>